"use client";

import { WeatherData, TempUnit, Language } from "@/types/weather";
import { getWeatherTheme } from "@/lib/weatherTheme";
import { t } from "@/lib/i18n";

interface WeatherCardProps {
  weather: WeatherData;
  unit: TempUnit;
  lang: Language;
}

function convertTemp(celsius: number, unit: TempUnit): number {
  if (unit === "fahrenheit") return Math.round((celsius * 9) / 5 + 32);
  return celsius;
}

function formatTemp(celsius: number, unit: TempUnit): string {
  const val = convertTemp(celsius, unit);
  return `${val}${unit === "fahrenheit" ? "°F" : "°C"}`;
}

function getUvLabel(uv: number): { label: string; color: string } {
  if (uv <= 2) return { label: "Low", color: "text-green-400" };
  if (uv <= 5) return { label: "Moderate", color: "text-yellow-400" };
  if (uv <= 7) return { label: "High", color: "text-orange-400" };
  if (uv <= 10) return { label: "Very High", color: "text-red-400" };
  return { label: "Extreme", color: "text-purple-400" };
}

export default function WeatherCard({ weather, unit, lang }: WeatherCardProps) {
  const {
    city,
    temperature,
    apparentTemperature,
    humidity,
    windSpeed,
    weatherDescription,
    weatherIcon,
    isDay,
    uvIndex,
    precipitationProbability,
    daily,
  } = weather;

  const theme = getWeatherTheme(weather.weatherCode, isDay);
  const uvInfo = getUvLabel(uvIndex);

  // Local time using Intl
  const localTimeStr = new Date().toLocaleTimeString("en-GB", {
    timeZone: city.timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const localDateStr = new Date().toLocaleDateString(
    lang === "ja" ? "ja-JP" : "en-GB",
    {
      timeZone: city.timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
    }
  );

  return (
    <div
      className={`rounded-2xl border ${theme.cardBorder} backdrop-blur-sm ${theme.cardBg} hover:border-white/20 transition-all duration-300 hover:scale-[1.01] flex flex-col`}
    >
      {/* Card Header */}
      <div className="p-6 pb-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="text-slate-400 text-xs font-medium uppercase tracking-widest">
            {city.country}
          </span>
          <span className="text-slate-400 text-xs">
            {isDay ? `☀️ ${t(lang, "day")}` : `🌙 ${t(lang, "night")}`}
          </span>
        </div>
        {/* City name */}
        <h2 className="text-2xl font-bold text-white">{city.nameJa}</h2>
        <p className="text-slate-300 text-sm mb-3">{city.name}</p>
        {/* Local Time */}
        <div className="text-white font-mono text-4xl font-bold tabular-nums leading-none">
          {localTimeStr}
        </div>
        <div className="text-slate-400 text-xs mt-1">{localDateStr}</div>
      </div>

      {/* Main Temperature */}
      <div className="px-6 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-thin text-white">
              {convertTemp(temperature, unit)}
              <span className="text-2xl text-slate-300">
                {unit === "fahrenheit" ? "°F" : "°C"}
              </span>
            </div>
            <div className="text-slate-400 text-sm mt-1">
              {t(lang, "feelsLike")} {formatTemp(apparentTemperature, unit)}
            </div>
          </div>
          <div className="text-6xl">{weatherIcon}</div>
        </div>
        <p className="text-blue-200 font-medium mt-3">{weatherDescription}</p>
      </div>

      {/* Stats Grid */}
      <div className="px-6 pb-4 grid grid-cols-2 gap-2">
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-slate-400 text-xs mb-1">{t(lang, "humidity")}</div>
          <div className="text-white font-semibold">{humidity}%</div>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-slate-400 text-xs mb-1">{t(lang, "wind")}</div>
          <div className="text-white font-semibold">{windSpeed} km/h</div>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-slate-400 text-xs mb-1">{t(lang, "uvIndex")}</div>
          <div className={`font-semibold ${uvInfo.color}`}>
            {uvIndex}{" "}
            <span className="text-xs opacity-80">{uvInfo.label}</span>
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-slate-400 text-xs mb-1">
            {t(lang, "precipitation")}
          </div>
          <div className="text-white font-semibold">
            {precipitationProbability}%
          </div>
        </div>
      </div>

      {/* 7-Day Forecast */}
      <div className="px-6 pb-6 flex-1">
        <div className="text-slate-400 text-xs font-medium uppercase tracking-widest mb-3">
          {t(lang, "forecast")}
        </div>
        <div className="space-y-1.5">
          {daily.slice(0, 7).map((day, i) => (
            <div
              key={day.date}
              className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2"
            >
              <div className="w-12 text-slate-300 text-sm font-medium">
                {i === 0
                  ? t(lang, "today")
                  : i === 1
                  ? t(lang, "tomorrow")
                  : lang === "ja"
                  ? day.weekdayJa
                  : day.weekday}
              </div>
              <div className="text-lg">{day.weatherIcon}</div>
              <div className="text-slate-400 text-xs w-8 text-center">
                💧{day.precipitationProbability}%
              </div>
              <div className="flex gap-2 text-sm min-w-[60px] justify-end">
                <span className="text-white font-medium">
                  {convertTemp(day.maxTemp, unit)}°
                </span>
                <span className="text-slate-500">
                  {convertTemp(day.minTemp, unit)}°
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
